import { Button, TextField } from '@efexteam/components';
import { useTranslation } from 'react-i18next';
import { INPUT_CONSTRAINTS, INPUT_MODES } from '@constants/inputs.constants';
import { VALIDATION_PATTERNS } from '@constants/patterns';
import { AuthLayout } from '../../../components/auth/AuthLayout';
import { AuthFormContainer } from '../../../components/auth/AuthFormContainer';
import { PasswordInput } from '../../../components/password';
import { PASSWORD_SCHEMA } from '../../../schemas/auth.schemas';
import usePasswordReset from '../hooks/usePasswordReset';

const CognitoPasswordForm = () => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    errors,
    isValid,
    success,
    error,
    isLoading,
    isEmailDisabled,
    isCodeDisabled,
    handleBackToLogin,
    onSubmit,
    getFieldError,
    scenarioContent,
    getEmailHelperText,
    getCodeHelperText,
    getSuccessMessage,
    fieldConfig,
  } = usePasswordReset();

  const headerActions = (
    <Button
      appearance="secondary"
      variant="outlined"
      color="primary"
      onClick={handleBackToLogin}
    >
      {t('backToLogin')}
    </Button>
  );

  return (
    <AuthLayout headerActions={headerActions}>
      <AuthFormContainer
        title={scenarioContent.title}
        description={scenarioContent.description}
        onSubmit={handleSubmit(onSubmit)}
        submitButtonText={t('resetPasswordSubmitButton') as string}
        submitButtonDisabled={!isValid}
        isLoading={isLoading}
        loadingText={t('resetPasswordSubmittingButton') as string}
        successMessage={success ? getSuccessMessage() : undefined}
        errorMessage={error}
      >
        {fieldConfig.showEmail && (
          <TextField
            name="email"
            inputRef={register('email')?.ref}
            onChange={register('email')?.onChange}
            onBlur={register('email')?.onBlur}
            fullWidth
            label={t('resetPasswordEmailLabel')}
            error={!!(errors as any).email}
            helperText={getEmailHelperText()}
            disabled={isEmailDisabled}
            required
            aria-label="email"
          />
        )}

        {fieldConfig.showCode && (
          <TextField
            name="code"
            inputRef={register('code')?.ref}
            onChange={register('code')?.onChange}
            onBlur={register('code')?.onBlur}
            fullWidth
            label={t('resetPasswordCodeLabel')}
            placeholder={t('resetPasswordCodePlaceholder') as string}
            error={!!(errors as any).code}
            helperText={getCodeHelperText()}
            disabled={isCodeDisabled}
            required
            aria-label="code"
            slotProps={{
              htmlInput: {
                maxLength: INPUT_CONSTRAINTS.CODE_LENGTH,
                pattern: VALIDATION_PATTERNS.NUMERIC_ONLY,
                inputMode: INPUT_MODES.NUMERIC,
              },
            }}
          />
        )}

        <PasswordInput
          control={control}
          setValue={setValue}
          name="password"
          confirmFieldName="confirmPassword"
          label={t('resetPasswordPasswordLabel') as string}
          confirmLabel={t('resetPasswordConfirmPasswordLabel') as string}
          error={!!errors.password}
          errorMessage={getFieldError('password')}
          confirmError={!!errors.confirmPassword}
          confirmErrorMessage={getFieldError('confirmPassword')}
          validationSchema={PASSWORD_SCHEMA}
          enforceValidation
        />
      </AuthFormContainer>
    </AuthLayout>
  );
};

export default CognitoPasswordForm;
